/**
 * API Data Transformer
 * Converts new API format (nodes with relationship objects) 
 * to existing app format (members with children/parents/spouse arrays)
 */

/**
 * Transform new API response to existing app format
 * @param {Object} apiResponse - Response from /api/family-tree/:slug (Axios response or data object)
 * @returns {Object} Transformed data compatible with existing components
 */
export const transformApiResponse = (apiResponse) => {
  // Handle Axios response wrapper
  const responseData = apiResponse?.data?.data || apiResponse?.data || apiResponse;

  if (!responseData?.nodes) {
    console.warn('Invalid API response format:', apiResponse);
    return { familyTree: {}, members: [] };
  }

  const { familyTree, nodes } = responseData;

  // Transform nodes to members format
  const members = nodes.map(node => transformNodeToMember(node));

  return {
    familyTree: {
      id: familyTree.id,
      slug: familyTree.slug,
      name: familyTree.name || familyTree.slug,
      description: familyTree.description,
      rootMemberId: familyTree.rootMemberId,
      visibility: familyTree.visibility,
    },
    members,
  };
};

/**
 * Transform a single node to member format
 * @param {Object} node - Node from API
 * @returns {Object} Member in app format
 */
const transformNodeToMember = (node) => {
  // Keep full parent/child/spouse/sibling arrays with relationship types
  // The ReactFamilyTreeView component handles both ID strings and {id, type} objects
  const parents = node.parents || [];
  const children = node.children || [];
  const spouses = node.spouses || [];
  const siblings = node.siblings || [];

  // Extract spouse ID (take first spouse if multiple) for backward compatibility
  const spouseId = node.spouses?.[0]?.id || null;

  // Transform documents to images format (documents is what comes from GET API)
  const images = node.documents?.map((doc) => ({
    id: doc.id, // Use the actual ID from API (UUID)
    url: doc.img || doc.link || doc.url,
    type: doc.type || 'gallery',
    caption: doc.caption || '',
    title: doc.title || 'Untitled',
    description: doc.desc || doc.description || '',
  })) || [];

  return {
    id: node.id,
    name: node.name,
    firstName: node.name?.split(' ')[0] || '',
    lastName: node.name?.split(' ').slice(1).join(' ') || '',
    gender: node.gender,
    birthDate: node.birthDate,
    birthPlace: node.birthPlace,
    deathDate: node.deathDate,
    deathPlace: node.deathPlace,
    occupation: node.occupation,
    description: node.description,
    profileImageUrl: node.profileImage,
    images,
    parents: parents,
    children: children,
    spouse: spouseId,
    spouses: spouses,
    siblings: siblings,
  };
};

/**
 * Transform member update data to API format
 * @param {Object} memberData - Member data from app
 * @returns {Object} Data in API format
 */
export const transformMemberToApi = (memberData) => {
  const { firstName, lastName, ...rest } = memberData;

  // Start with required fields
  const apiData = {};

  // Name is required
  const fullName = `${firstName || ''} ${lastName || ''}`.trim() || memberData.name;
  if (fullName) {
    apiData.name = fullName;
  }

  // Gender is required (only include if present)
  if (rest.gender) {
    apiData.gender = rest.gender;
  }

  // Only add dates if they are valid and not empty
  if (rest.birthDate && rest.birthDate !== 'Invalid date' && rest.birthDate !== '') {
    apiData.birthDate = rest.birthDate;
  }
  if (rest.deathDate && rest.deathDate !== 'Invalid date' && rest.deathDate !== '') {
    apiData.deathDate = rest.deathDate;
  }

  // Add optional text fields only if they have values
  if (rest.birthPlace && rest.birthPlace.trim && rest.birthPlace.trim()) {
    apiData.birthPlace = rest.birthPlace.trim();
  }
  if (rest.deathPlace && rest.deathPlace.trim && rest.deathPlace.trim()) {
    apiData.deathPlace = rest.deathPlace.trim();
  }
  if (rest.occupation && rest.occupation.trim && rest.occupation.trim()) {
    apiData.occupation = rest.occupation.trim();
  }
  if (rest.description && rest.description.trim && rest.description.trim()) {
    apiData.description = rest.description.trim();
  }
  if (rest.profileImageUrl && rest.profileImageUrl.trim && rest.profileImageUrl.trim()) {
    apiData.profileImage = rest.profileImageUrl.trim();
  }

  // Add mobile if present
  if (rest.mobile && rest.mobile.trim && rest.mobile.trim()) {
    apiData.mobile = rest.mobile.trim();
  }

  // Transform images array ONLY if explicitly provided with items
  // Don't include images array when only removeImages is present (deletion only)
  if (rest.images !== undefined && rest.images.length > 0) {
    apiData.images = rest.images.map(image => {
      const imageData = {
        url: image.url
      };

      // Add optional fields if they exist
      if (image.type) imageData.type = image.type;
      if (image.caption) imageData.caption = image.caption;
      if (image.title) imageData.title = image.title;
      if (image.description) imageData.description = image.description;

      return imageData;
    });
  }

  // Add removeImages array if present (for image deletion)
  if (rest.removeImages && rest.removeImages.length > 0) {
    apiData.removeImages = rest.removeImages;
  }

  // Preserve relationship arrays if they exist (but not empty arrays)
  if (rest.spouses && rest.spouses.length > 0) {
    apiData.spouses = rest.spouses;
  }
  if (rest.parents && rest.parents.length > 0) {
    apiData.parents = rest.parents;
  }
  if (rest.children && rest.children.length > 0) {
    apiData.children = rest.children;
  }
  if (rest.siblings && rest.siblings.length > 0) {
    apiData.siblings = rest.siblings;
  }

  return apiData;
};

/**
 * Transform images to documents format for API
 * @param {Array} images - Images from app
 * @returns {Array} Documents in API format
 */
export const transformImagesToDocuments = (images) => {
  const documents = images?.map((image, index) => {
    // Preserve existing id if available, otherwise generate new one
    const doc = {
      img: image.url,
      link: image.url,
      title: image.title || 'Untitled',
      desc: image.description || '',
    };

    // Preserve the id if it exists in the original image data
    if (image.id) {
      doc.id = image.id;
    }

    return doc;
  }) || [];

  console.log('Transforming images to documents:', images, '→', documents);
  return documents;
};

/**
 * Transform relationships for API
 * @param {Object} relationshipData - Relationship data from app
 * @returns {Object} Relationship in API format
 */
export const transformRelationshipToApi = (relationshipData) => {
  const { member1Id, member2Id, type } = relationshipData;

  // Map app relationship types to API types
  const typeMapping = {
    'parent': 'parent',
    'child': 'child',
    'spouse': 'spouse',
    'sibling': 'sibling',
    'step-parent': 'step-parent',
    'step-child': 'step-child',
    'adopted': 'adopted-parent',
  };

  return {
    member1Id,
    member2Id,
    relationshipType: typeMapping[type] || type,
    metadata: { type: relationshipData.relationshipType || 'blood' },
  };
};

export default {
  transformApiResponse,
  transformNodeToMember,
  transformMemberToApi,
  transformImagesToDocuments,
  transformRelationshipToApi,
};
